import { Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomTable from "../../../components/CustomTable";
import CustomTextField from "../../../components/CustomTextField";
import CustomSelect from "../../../components/Select/Select";
import { getTransactionHistoryByStockId } from "../../../redux/reducers/clinicSlice";
import { AppColors } from "../../../util/AppColors";
import { transactionTypeList } from "../../../util/dropList";
import { baseURL } from "../../../util/server";

const tblHdrs = [
  "date",
  "tranType",
  "customerSupplier",
  "invoiceNumber",
  "qty",
];

const initSearch = {
  startDate: null,
  endDate: null,
  type: "all",
  supplierName: "",
  customerName: "",
  petName: "",
};

const TransactionHistory = ({
  selectedStock = null,
  initialStartDate = null,
  initialEndDate = null,
  initType = null,
}) => {
  const dispatch = useDispatch();
  const [searchValues, setSearchValues] = useState({
    ...initSearch,
    ...(initialStartDate ? { startDate: initialStartDate } : {}),
    ...(initialEndDate ? { endDate: initialEndDate } : {}),
    ...(initType ? { type: initType } : {}),
  });
  const [page, setPage] = useState(1);
  const { stockTransHistory, details: clinicDetail } = useSelector(
    (state) => state?.clinic,
  );
  const tranData = stockTransHistory?.data ?? {};
  const itemSize = 10;
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { clinic: clinicData } = useMemo(() => {
    if (!mandateDataRaw) {
      return { clinic: {} };
    }
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        clinic: reqData?.clinic ?? {},
      };
    } catch {
      return { clinic: {} };
    }
  }, [mandateDataRaw]);
  const clinicDet = clinicData?.id ? clinicData : clinicDetail;

  useEffect(() => {
    onApiCall();
  }, [
    selectedStock?.stockId,
    searchValues?.startDate,
    searchValues?.endDate,
    searchValues?.type,
    searchValues?.supplierName,
    searchValues?.customerName,
    searchValues?.petName,
  ]);

  const onApiCall = (pge = 1, reset = false) => {
    if (!selectedStock?.stockId) return;
    setPage(pge);
    let reqUrl = `?page=${pge}&itemSize=${itemSize}`;
    if (!reset) {
      if (searchValues?.startDate)
        reqUrl += `&startDate=${moment(new Date(searchValues?.startDate)).format("YYYY-MM-DD")}`;
      if (searchValues?.endDate)
        reqUrl += `&endDate=${moment(new Date(searchValues?.endDate)).format("YYYY-MM-DD")}`;
      if (searchValues?.type) reqUrl += `&type=${searchValues?.type}`;
      if (searchValues?.supplierName?.length > 0)
        reqUrl += `&supplierName=${searchValues?.supplierName}`;
      if (searchValues?.customerName?.length > 0)
        reqUrl += `&userName=${searchValues?.customerName}`;
      if (searchValues?.petName?.length > 0)
        reqUrl += `&petName=${searchValues?.petName}`;
    }
    dispatch(
      getTransactionHistoryByStockId({
        id: selectedStock?.stockId,
        url: reqUrl,
      }),
    );
  };

  const onReset = () => {
    flushSync(() => setSearchValues(initSearch));
  };

  const handleChangePage = (e, selectedPage) => {
    onApiCall(selectedPage);
  };

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    setSearchValues({
      ...searchValues,
      [name?.trim()?.replace(/,$/, "")]: value, // remove spaces & trailing commas
    });
  };

  const getTableList = () => {
    return (tranData?.transactions ?? [])?.map((li) => ({
      ...li,
      tranType: li?.type,
      date: moment(new Date(li?.date)).format("DD MMM YYYY"),
      customerSupplier: li?.userName ?? li?.supplierName ?? "",
      qty: `${li?.quantity}${li?.uom?.length > 0 ? ` ${li?.uom}` : ""}`,
    }));
  };

  const onDownloadReport = () => {
    const params = new URLSearchParams();
    if (clinicDet?.id) params.append("clinicId", clinicDet.id);
    if (searchValues?.startDate) {
      params.append(
        "startDate",
        moment(new Date(searchValues?.startDate)).format("YYYY-MM-DD"),
      );
    }
    if (searchValues?.endDate) {
      params.append(
        "endDate",
        moment(new Date(searchValues?.endDate)).format("YYYY-MM-DD"),
      );
    }
    if (searchValues?.type) params.append("type", searchValues?.type);
    if (searchValues?.supplierName?.length > 0) {
      params.append("supplierName", searchValues?.supplierName);
    }
    if (searchValues?.customerName?.length > 0) {
      params.append("userName", searchValues?.customerName);
    }
    if (searchValues?.petName?.length > 0) {
      params.append("petName", searchValues?.petName);
    }
    const queryString = params.toString();
    const reqUrl = `${baseURL}/clinic/stock/${selectedStock?.stockId}/transaction-history/export${
      queryString ? `?${queryString}` : ""
    }`;
    window.open(reqUrl, "_blank");
  };

  return (
    <>
      {/* <TopBar> */}
      <div className="mt20">
        <div className="flex-row">
          <div className="top-row-cen-con w100Per">
            <div className="w13Per">
              <CustomTextField
                placeholder="Start Date"
                fullWidth
                name="startDate"
                handleChange={onSearchChange}
                value={searchValues?.startDate}
                backgroundColor={AppColors.lightBlue2}
                label="Start Date"
                mobileDate
              />
            </div>
            <div className="w13Per ml15">
              <CustomTextField
                placeholder="End Date"
                fullWidth
                name="endDate"
                handleChange={onSearchChange}
                value={searchValues?.endDate}
                backgroundColor={AppColors.lightBlue2}
                label="End Date"
                mobileDate
              />
            </div>
            <div className="w13Per ml15">
              <CustomSelect
                list={transactionTypeList}
                fullWidth
                name="type"
                handleChange={onSearchChange}
                value={searchValues?.type}
                backgroundColor={AppColors.lightBlue2}
                label="Type"
                select
              />
            </div>
            <div className="w13Per ml15">
              <CustomTextField
                placeholder="Supplier Name"
                fullWidth
                name="supplierName"
                handleChange={onSearchChange}
                value={searchValues?.supplierName}
                backgroundColor={AppColors.lightBlue2}
                label="Supplier Name"
              />
            </div>
            <div className="w13Per ml15">
              <CustomTextField
                placeholder="Customer Name"
                fullWidth
                name="customerName"
                handleChange={onSearchChange}
                value={searchValues?.customerName}
                backgroundColor={AppColors.lightBlue2}
                label="Customer Name"
              />
            </div>
            <div className="w13Per ml15">
              <CustomTextField
                placeholder="Pet Name"
                fullWidth
                name="petName"
                handleChange={onSearchChange}
                value={searchValues?.petName}
                backgroundColor={AppColors.lightBlue2}
                label="Pet Name"
              />
            </div>

            <div className="ml15">
              <CustomButton text="Reset" onClick={onReset} smallBtn redBtn />
            </div>
            <div className="ml15">
              <CustomButton text="⬇" onClick={onDownloadReport} smallBtn />
            </div>
          </div>
        </div>
        <div className="flex-row-ali-cen mt10 mh30">
          <Typography className="txt-semi-bold fs14">
            {tranData?.tradeName ?? ""}
          </Typography>
          <Typography className="txt-semi-bold fs14 ml15">
            {tranData?.productName ?? ""}
          </Typography>
        </div>
        <div className="flex-row-ali-cen mh30">
          <Typography className="font-medium fs14">
            Current stock Qty: {tranData?.currentStockQty ?? ""}
          </Typography>
          <Typography className="font-medium fs14 ml15">
            Total purchase Qty: {tranData?.totalPurchaseQty ?? ""}
          </Typography>
          <Typography className="font-medium fs14 ml15">
            Total sales Qty: {tranData?.totalSalesQty ?? ""}
          </Typography>
        </div>
      </div>
      <Typography className="font-medium fs12 gray28 mt10 mh30">
        Number of records: {stockTransHistory?.totalRecords ?? 0}
      </Typography>
      <div className="mtMin10">
        <CustomTable
          grey
          columns={tblHdrs}
          datas={getTableList()}
          page={page}
          rowsPerPage={itemSize}
          totalRecords={stockTransHistory?.totalRecords ?? 0}
          handleChangePage={handleChangePage}
        />
      </div>
    </>
  );
};

export default TransactionHistory;
